/**
 * Cola editorial del blog. Humano + Claude in Chrome: estados, completitud, miniatura.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileUp, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import {
    useBlogPostsList,
    useBlogPostsStats,
    useBlogTypes,
    useDeleteBlogPost,
} from '../hooks/useBlogAdmin';
import type { BlogPostListItem } from '../types';
import { resolveBlogImageUrl } from '../lib/resolveBlogImageUrl';
import { BLOG_STATUS_HINTS } from '../lib/statusLabels';
import { BlogCompleteness } from './BlogCompleteness';
import { BlogStatusBadge } from './BlogStatusBadge';

const PAGE_SIZE = 24;

function PostCard({ row, onOpen, onDelete }: { row: BlogPostListItem; onOpen: () => void; onDelete: () => void }) {
    const img = resolveBlogImageUrl(row.main_image_url);
    return (
        <div
            className={cn(
                'text-left rounded-xl border bg-card hover:border-primary/50 hover:shadow-md transition-all overflow-hidden',
                row.status !== 'enabled' && 'opacity-90',
            )}
        >
            <button type="button" onClick={onOpen} className="w-full text-left">
                <div className="aspect-[16/9] bg-muted/40 overflow-hidden">
                    {img ? (
                        <img src={img} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-xs text-muted-foreground">
                            Sin imagen
                        </div>
                    )}
                </div>
                <div className="p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-sm leading-snug line-clamp-2">{row.title}</h3>
                        <BlogStatusBadge status={row.status} size="sm" />
                    </div>
                    {row.status === 'enabled' && !(row.main_image_url || '').trim() && (
                        <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            Publicada sin portada
                        </span>
                    )}
                    <p className="text-[11px] text-muted-foreground truncate">
                        {row.type_name || 'Sin tipología'} · /{row.slug}
                    </p>
                    <BlogCompleteness
                        compact
                        flags={{
                            hasExcerpt: row.has_excerpt,
                            hasBody: row.has_body,
                            hasImage: row.has_image,
                            hasSeo: row.has_seo,
                            completeness: row.completeness,
                        }}
                    />
                    {row.is_rich && (
                        <span className="text-[10px] text-sky-700 dark:text-sky-300">HTML rico (no usar bloques)</span>
                    )}
                </div>
            </button>
            <div className="px-3 pb-3 flex justify-end">
                <button
                    type="button"
                    onClick={onDelete}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title="Eliminar"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

export function BlogPostsQueue() {
    const navigate = useNavigate();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeId, setTypeId] = useState<string | null>(null);
    const [incompleteOnly, setIncompleteOnly] = useState(false);
    const [page, setPage] = useState(0);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        setPage(0);
    }, [search, statusFilter, typeId, incompleteOnly]);

    const filters = useMemo(
        () => ({
            statusFilter,
            typeId,
            search,
            incompleteOnly,
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
        }),
        [statusFilter, typeId, search, incompleteOnly, page],
    );

    const statsQ = useBlogPostsStats();
    const listQ = useBlogPostsList(filters);
    const typesQ = useBlogTypes();
    const deleteMut = useDeleteBlogPost();
    const stats = statsQ.data;
    const items = listQ.data?.items ?? [];
    const total = listQ.data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="space-y-5" data-page="blog-posts-queue">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h2 className="text-lg font-bold tracking-tight">Entradas</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Cola de trabajo. Abre un artículo, mira la vista previa de landing y publícalo.
                        La IA puede leer esta pantalla y subir un lote de .html.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 self-start">
                    <Link
                        to="/blog/posts/new"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border hover:bg-muted/50"
                    >
                        <Plus size={14} />
                        Nueva
                    </Link>
                    <Link
                        to="/blog/posts/import"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                    >
                        <FileUp size={14} />
                        Importar lote
                    </Link>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {(
                    [
                        { id: 'all', label: 'Todas', value: stats?.total },
                        { id: 'draft', label: 'Borrador', value: stats?.draft, hint: BLOG_STATUS_HINTS.draft },
                        { id: 'review', label: 'En revisión', value: stats?.review, hint: BLOG_STATUS_HINTS.review },
                        { id: 'enabled', label: 'Publicadas', value: stats?.enabled, hint: BLOG_STATUS_HINTS.enabled },
                    ] as const
                ).map((chip) => (
                    <button
                        key={chip.id}
                        type="button"
                        title={'hint' in chip ? chip.hint : 'Todos los estados'}
                        onClick={() => setStatusFilter(chip.id)}
                        className={cn(
                            'rounded-xl border px-3 py-2.5 text-left transition-colors',
                            statusFilter === chip.id
                                ? 'border-primary bg-primary/10'
                                : 'border-border bg-card hover:bg-muted/40',
                        )}
                    >
                        <div className="text-[11px] text-muted-foreground">{chip.label}</div>
                        <div className="text-xl font-bold tabular-nums">
                            {chip.value == null ? '—' : chip.value}
                        </div>
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        placeholder="Buscar título, slug…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                </div>
                <select
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={typeId ?? ''}
                    onChange={(e) => setTypeId(e.target.value || null)}
                >
                    <option value="">Todas las tipologías</option>
                    {(typesQ.data ?? []).map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={incompleteOnly}
                        onChange={(e) => setIncompleteOnly(e.target.checked)}
                    />
                    Solo incompletas ({stats?.incomplete ?? '—'})
                </label>
            </div>

            {listQ.isError && (
                <p className="text-sm text-destructive">{(listQ.error as Error).message || 'Error al cargar'}</p>
            )}

            {listQ.isLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Cargando entradas…
                </div>
            ) : items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">
                    No hay entradas con estos filtros.
                </p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {items.map((row) => (
                        <PostCard
                            key={row.id}
                            row={row}
                            onOpen={() => navigate(`/blog/posts/${row.id}`)}
                            onDelete={() => setDeleteId(row.id)}
                        />
                    ))}
                </div>
            )}

            <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                    {total} entradas · pág. {page + 1}/{pageCount}
                </span>
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-md border disabled:opacity-40"
                        disabled={page <= 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                        Anterior
                    </button>
                    <button
                        type="button"
                        className="px-3 py-1.5 rounded-md border disabled:opacity-40"
                        disabled={page + 1 >= pageCount}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Siguiente
                    </button>
                </div>
            </div>

            <AdminConfirmDialog
                open={!!deleteId}
                variant="danger"
                title="Eliminar entrada"
                description={<p>¿Eliminar esta entrada del blog? Esta acción no se puede deshacer.</p>}
                confirmLabel="Eliminar"
                onCancel={() => setDeleteId(null)}
                onConfirm={() => {
                    if (!deleteId) return;
                    deleteMut.mutate(deleteId, {
                        onSuccess: () => setDeleteId(null),
                    });
                }}
                confirmDisabled={deleteMut.isPending}
            />
        </div>
    );
}
